import { triggerAiAction, restorePersistedHighlights, highlightActiveAiControls } from "../tiptap/ai_actions"
import { buildPrintGuard } from "../tiptap/print_guard"
import { buildToolbar, bindToolbarButton, executeToolbarCommand, updateToolbarState, updateTableToolbar } from "../tiptap/toolbar"
import { Controller } from "stimulus"
import { Editor } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import ImageUpload from "../tiptap/extensions/image_upload"
import AiHighlight from "../tiptap/extensions/ai_highlight"
import Code from "@tiptap/extension-code"
import BubbleMenu from "@tiptap/extension-bubble-menu"
import { Table } from "@tiptap/extension-table"
import { TableRow } from "@tiptap/extension-table-row"
import { TableCell } from "@tiptap/extension-table-cell"
import { TableHeader } from "@tiptap/extension-table-header"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import TextAlign from "@tiptap/extension-text-align"
import TaskList from "@tiptap/extension-task-list"
import TaskItem from "@tiptap/extension-task-item"
import { PaginationPlus } from "tiptap-pagination-plus"
import { SearchHighlight } from "../tiptap/extensions/search_highlight"
import CommentHighlight from "../tiptap/extensions/comment_highlight"
import { openDocumentFind } from "../tiptap/find_replace"
import { addCommentToMenu, wireCommentHighlightClicks } from "../tiptap/comments"
// Section folding disabled — CSS-only approach too slow for 23K+ node documents.
// Document splitting (separate editor per section) is the proper solution.
// import { SectionFold } from "../tiptap/extensions/section_fold"

export default class extends Controller {
  static values = { content: String }

  connect() {
    this._selectionThrottle = false
    console.log("[Tiptap] Controller connected")
    this.buildFixedToolbar()
    this.buildBubbleMenu()
    this.buildTableToolbar()
    this.initEditor()
    this.setupFormSync()
    this._printGuard = buildPrintGuard(document.getElementById("publishForm"), {
      sync: () => this.syncToHiddenField(this.editor),
      onStatus: message => this._toolbar?.setPrintStatus(message),
    })

    // Listen for rebuild event after AI flow completes
    this._rebuildHandler = () => this.rebuildEditor()
    this.element.addEventListener("tiptap:rebuild", this._rebuildHandler)

    // Watch for format changes on the left-column to toggle pagination
    const leftColumn = document.getElementById("left-column")
    if (leftColumn) {
      this._formatObserver = new MutationObserver(() => {
        const needsPagination = leftColumn.classList.contains("editor-format-print") && !this._isLargeDocument
        const hasPagination = !!this.editor?.extensionManager?.extensions?.find(e => e.name === "PaginationPlus")
        if (needsPagination !== hasPagination) {
          this.reinitWithFormat()
        }
      })
      this._formatObserver.observe(leftColumn, { attributes: true, attributeFilter: ["class"] })
    }
  }

  disconnect() {
    clearTimeout(this._syncDebounce)
    clearTimeout(this._highlightTimeout)
    cancelAnimationFrame(this._selectionFrame)
    this._selectionThrottle = false
    this.element.removeEventListener("tiptap:rebuild", this._rebuildHandler)
    if (this._form) this._form.removeEventListener("submit", this._onSubmit, true)
    if (this._printGuard) this._printGuard.destroy()
    if (this._toolbar) this._toolbar.destroy()
    if (this.tableToolbar) this.tableToolbar.remove()
    if (this.editor) {
      if (!this.editor.isDestroyed) this.syncToHiddenField(this.editor)
      if (window.tiptapEditor === this.editor) window.tiptapEditor = null
      this.editor.destroy()
    }
    if (window._syncAndSaveEditor === this._syncAndSaveEditor) window._syncAndSaveEditor = null
    if (this.bubbleEl) this.bubbleEl.remove()
    if (this.linkFormEl) this.linkFormEl.remove()
    if (this._findBar) this._findBar.close()
    if (this._formatObserver) this._formatObserver.disconnect()
    window.removeEventListener("beforeunload", this._beforeUnload)
  }

  showFindBar(withReplace = false) {
    const container = this.element.closest(".left-column") || this.element
    this._findBar = openDocumentFind(container, () => [this.editor], { showReplace: withReplace })
  }

  // ==========================================
  // Fixed Formatting Toolbar (below editor header)
  // ==========================================

  buildFixedToolbar() {
    const editorContent = this.element.closest(".editor-content")
    if (!editorContent) return
    if (this._toolbar) this._toolbar.destroy()
    this._toolbar = buildToolbar(editorContent, () => this.editor, { beforePrint: () => this._printGuard ? this._printGuard.check() : true, onFind: () => this.showFindBar() })
    this.fixedToolbar = this._toolbar.toolbar
    this.linkFormEl = this._toolbar.state.linkFormEl
  }

  // ==========================================
  // Bubble Menu (AI actions on text selection)
  // ==========================================

  buildBubbleMenu() {
    this.bubbleEl = document.createElement("div")
    this.bubbleEl.className = "tiptap-bubble-menu"

    // AI action button with dropdown
    const aiBtn = document.createElement("button")
    aiBtn.type = "button"
    aiBtn.className = "tiptap-toolbar-btn tiptap-ai-btn"
    aiBtn.title = "AI Actions"
    aiBtn.innerHTML = '<span class="fas fa-wand-magic-sparkles"></span>'
    bindToolbarButton(aiBtn, (e) => {
      e.preventDefault()
      this.toggleAiDropdown()
    })
    this.bubbleEl.appendChild(aiBtn)

    // AI dropdown
    this.aiDropdown = document.createElement("div")
    this.aiDropdown.className = "tiptap-ai-dropdown"
    this.aiDropdown.style.display = "none"

    const actions = [
      { action: "rewrite", icon: "fa-pen", label: "Rewrite" },
      { action: "expand", icon: "fa-expand", label: "Expand" },
      { action: "simplify", icon: "fa-compress", label: "Simplify" },
      { action: "summarize", icon: "fa-align-left", label: "Summarize" },
    ]
    actions.forEach(a => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.innerHTML = `<span class="fas ${a.icon} mr-2"></span>${a.label}`
      bindToolbarButton(btn, (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.triggerAiAction(a.action)
      })
      this.aiDropdown.appendChild(btn)
    })
    this.bubbleEl.appendChild(this.aiDropdown)

    // Comment button
    addCommentToMenu(this.bubbleEl, () => ({ editor: this.editor, editorEl: this.element }))

    document.body.appendChild(this.bubbleEl)
  }

  toggleAiDropdown() {
    const showing = this.aiDropdown.style.display !== "none"
    this.aiDropdown.style.display = showing ? "none" : "flex"
  }

  aiContext() {
    if (!this._aiCtx || this._aiCtx.editor !== this.editor) {
      this._aiCtx = {
        editor: this.editor,
        editorEl: this.element,
        containerEl: this.element.closest('.editor-content'),
        aiDropdownEl: this.aiDropdown,
        syncToField: () => this.syncToHiddenField(this.editor),
      }
    }
    return this._aiCtx
  }

  triggerAiAction(action) {
    return triggerAiAction(this.editor, action, this.aiContext())
  }

  restorePersistedHighlights() {
    restorePersistedHighlights(this.editor, this.element, this.aiContext())
  }

  highlightActiveAiControls() {
    highlightActiveAiControls(this.element)
  }

  // ==========================================
  // Commands
  // ==========================================

  executeCommand(cmd) {
    executeToolbarCommand(cmd, () => this.editor, this._toolbar.state)
  }

  showLinkForm() {
    const input = this.linkFormEl.querySelector(".tiptap-link-input")
    input.value = this.editor.getAttributes("link").href || ""
    this.linkFormEl.style.display = "flex"
    setTimeout(() => input.focus(), 10)
  }

  hideLinkForm() {
    this.linkFormEl.style.display = "none"
    this.editor.chain().focus().run()
  }

  buildTableToolbar() {
    this.tableToolbar = this._toolbar?.tableToolbar
  }

  updateTableToolbar() {
    updateTableToolbar(this.tableToolbar, this.editor)
  }

  updateToolbarState() {
    updateToolbarState(this.fixedToolbar, this.editor)
  }

  // ==========================================
  // Editor Init
  // ==========================================

  setupFormSync() {
    this._beforeUnload = () => {
      this.syncToHiddenField(this.editor)
      const form = document.getElementById("publishForm")
      if (form) {
        const formData = new FormData(form)
        navigator.sendBeacon(form.action, formData)
      }
    }
    window.addEventListener("beforeunload", this._beforeUnload)

    const form = document.getElementById("publishForm")
    if (form) {
      this._form = form
      this._onSubmit = () => this.syncToHiddenField(this.editor)
      form.addEventListener("submit", this._onSubmit, true)
    }
  }

  rebuildEditor() {
    cancelAnimationFrame(this._selectionFrame)
    this._selectionThrottle = false
    clearTimeout(this._syncDebounce)
    clearTimeout(this._highlightTimeout)
    if (this.editor && !this.editor.isDestroyed) this.editor.destroy()
    this.element.querySelectorAll(".ProseMirror").forEach(el => el.remove())
    if (this.bubbleEl) this.bubbleEl.remove()
    this.buildBubbleMenu()
    this.initEditor()
  }

  reinitWithFormat() {
    if (!this.editor) return
    this.syncToHiddenField(this.editor)
    this.rebuildEditor()
  }

  get isPrintFormat() {
    const leftColumn = document.getElementById("left-column")
    return leftColumn && leftColumn.classList.contains("editor-format-print")
  }

  initEditor() {
    const hiddenField = document.getElementById("post_body")
    const initialContent = hiddenField ? hiddenField.value : (this.contentValue || "")

    // Build extensions list
    const extensions = [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
        code: false,
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        linkOnPaste: true,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      Placeholder.configure({ placeholder: "Start writing..." }),
      ImageUpload.configure({ inline: false, allowBase64: false }),
      AiHighlight,
      Code,
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Subscript,
      Superscript,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      SearchHighlight,
      CommentHighlight,
    ]

    // Large document detection (~125+ pages)
    const isLargeDocument = initialContent.length > 500000
    this._isLargeDocument = isLargeDocument

    // Add pagination for print format (skip for very large documents — too expensive)
    if (this.isPrintFormat && !isLargeDocument) {
      extensions.push(
        PaginationPlus.configure({
          pageHeight: 1056,   // 11in * 96dpi
          pageWidth: 816,     // 8.5in * 96dpi
          marginTop: 38,      // ~0.4in browser default margin
          marginBottom: 38,
          marginLeft: 38,
          marginRight: 38,
          contentMarginTop: 0,
          contentMarginBottom: 0,
          pageGap: 8,
          pageGapBorderSize: 0,
          pageBreakBackground: "#ffffff",
          pageGapBorderColor: "#ddd",
          footerRight: "{page}",
          footerLeft: "",
          headerRight: "",
          headerLeft: "",
        })
      )
    }

    extensions.push(
      BubbleMenu.configure({
          element: this.bubbleEl,
          shouldShow: ({ editor, from, to, state }) => {
            if (window._tiptapAiProcessing) return false
            if (from === to) return false
            // Don't show for node selections (images, etc.)
            if (state.selection.node) return false
            // Don't show inside a pending AI highlight
            const resolved = editor.state.doc.resolve(from)
            if (resolved.marks().some(m => m.type.name === "aiHighlight")) return false
            return true
          },
          tippyOptions: {
            duration: [150, 100],
            placement: "top",
            onError: () => {},
          },
        })
    )

    this.editor = new Editor({
      element: this.element,
      extensions,
      content: initialContent,
      editorProps: {
        attributes: {
          class: "editable body post-formatting",
          "aria-label": "Post Body",
          // Disable browser spellcheck on large documents — checking 10K+ paragraphs
          // causes significant keystroke lag
          spellcheck: isLargeDocument ? "false" : "true",
        },
        // Custom keyboard shortcuts
        handleKeyDown: (view, event) => {
          if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
            event.preventDefault()
            this.showLinkForm()
            return true
          }
          // Cmd+F = Find, Cmd+Shift+F = Find & Replace
          if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "f") {
            event.preventDefault()
            this.showFindBar(event.shiftKey)
            return true
          }
          // Cmd+Shift+H = Heading 2
          if (event.metaKey && event.shiftKey && event.key === "H") {
            this.editor.chain().focus().toggleHeading({ level: 2 }).run()
            return true
          }
          // Cmd+Shift+B = Blockquote
          if (event.metaKey && event.shiftKey && event.key === "B") {
            this.editor.chain().focus().toggleBlockquote().run()
            return true
          }
          // Cmd+Shift+J = Bullet List
          if (event.metaKey && event.shiftKey && event.key === "J") {
            this.editor.chain().focus().toggleBulletList().run()
            return true
          }
          // Cmd+Shift+L = Ordered List
          if (event.metaKey && event.shiftKey && event.key === "L") {
            this.editor.chain().focus().toggleOrderedList().run()
            return true
          }
          // Cmd+Shift+X = Strikethrough
          if (event.metaKey && event.shiftKey && event.key === "X") {
            this.editor.chain().focus().toggleStrike().run()
            return true
          }
          return false
        },
      },
      onUpdate: ({ editor }) => {
        document.dispatchEvent(new CustomEvent("document-save:changed", { detail: { key: "document" } }))
        this.scheduleToolbarUpdate()
        // Debounce sync and autosave for large documents
        clearTimeout(this._syncDebounce)
        this._syncDebounce = setTimeout(() => {
          this.syncToHiddenField(editor)
          this.keepAutosaveAlive()
          document.dispatchEvent(new CustomEvent("tiptap:content-changed"))
        }, 1000)
      },
      onCreate: ({ editor }) => {
        window.tiptapEditor = editor
        document.dispatchEvent(new CustomEvent("tiptap:editor-ready"))
        this._syncAndSaveEditor = () => {
          this.syncToHiddenField(editor)
          this.keepAutosaveAlive()
        }
        window._syncAndSaveEditor = this._syncAndSaveEditor
        // Restore controls for persisted AI highlights (defer for large documents)
        const highlightDelay = isLargeDocument ? 3000 : 500
        this._highlightTimeout = setTimeout(() => this.restorePersistedHighlights(), highlightDelay)
        wireCommentHighlightClicks(this.element.querySelector(".ProseMirror"))
      },
      onSelectionUpdate: () => this.scheduleToolbarUpdate(),
    })
  }

  scheduleToolbarUpdate() {
    // Throttle toolbar updates to prevent lag on large documents
    if (this._selectionThrottle) return
    this._selectionThrottle = true
    this._selectionFrame = requestAnimationFrame(() => {
      this.updateToolbarState()
      this.highlightActiveAiControls()
      this.updateTableToolbar()
      this._selectionThrottle = false
    })
  }

  syncToHiddenField(editor) {
    const hiddenField = document.getElementById("post_body")
    if (!hiddenField || !editor) return

    // Content is always fully in ProseMirror (folding is CSS-only, not node replacement).
    // getHTML() returns the complete document regardless of fold state.
    let html = editor.getHTML()
    html = html.replace(/ data-chnode="[^"]*"/g, "")
    hiddenField.value = html
  }

  keepAutosaveAlive() {
    document.dispatchEvent(new KeyboardEvent("keyup", { key: "a", bubbles: true }))
  }
}
